import React, { useRef } from 'react';
import type { SxProps, Theme } from '@mui/material/styles';


import { Stack } from 'ui/stack';
import { Box } from 'ui/box';
import { mergeSx } from 'ui/merge-sx';

import MetricBlock from 'containers/user/goals/goals-monitoring/monitoring-chart/components/metric-block';
import PlaceholderBlock from 'containers/user/goals/goals-monitoring/monitoring-chart/components/placeholder-block';

import Icons from 'assets/icons';

import type { CEBlock } from 'containers/user/goals/goals-monitoring/monitoring-chart/hooks/use-goals-monitoring';

const CHART_SX: SxProps<Theme> = {
  position: 'relative',
  bgcolor: 'common.white',
  p: 3,
  borderRadius: '0.5rem',
  borderWidth: '1px',
  borderColor: 'tokens.navy.200',
};

const isRateMetric = (key: string): boolean =>
  key.toLowerCase().includes('rate');

interface CriticalElementChartProps {
  blocks?: CEBlock[];
  previousYear?: string | number;
  currentYear?: string | number;
  cohortName?: string;
  sx?: SxProps<Theme>;
  chartId?: string;
  isRestricted?: boolean;
}

const CriticalElementChart = ({
  blocks = [],
  previousYear,
  currentYear,
  cohortName,
  sx,
  chartId,
  isRestricted = false,
}: CriticalElementChartProps): React.JSX.Element | null => {
  const chartRef = useRef<HTMLDivElement>(null);

  if (!blocks.length) return null;

  return (
    <Box id={chartId} sx={mergeSx(CHART_SX, sx)} ref={chartRef}>
      <Stack direction="row" sx={{ gap: 4 }}>
        {blocks.map((block, index) => {
          const isPlaceholder = isRestricted && index < 2;

          if (isPlaceholder) {
            return (
              <Box key={index} sx={{ flex: '1 1 0%', maxWidth: '200px' }}>
                <PlaceholderBlock
                  metricKey={block.key}
                  previousYear={previousYear}
                  currentYear={currentYear}
                />
              </Box>
            );
          }

          const isRate = isRateMetric(block.key);

          const scaleIfRate = (
            num: number | null | undefined
          ): number | null | undefined =>
            isRate ? (num as number) * 100 : num;

          const primary = scaleIfRate(block.previous);
          const secondary = scaleIfRate(block.value);

          const metricConfig: {
            unit?: string;
            change: number | null | undefined;
            cohortChange: number | null | undefined;
          } = isRate
            ? {
                unit: '%',
                change: scaleIfRate(block.value),
                cohortChange: scaleIfRate(block.cohort),
              }
            : {
                change: block.percentageChange ?? null,
                cohortChange: block.cohortPercentageChange ?? null,
              };

          return (
            <Box key={index} sx={{ flex: '1 1 0%', maxWidth: '200px' }}>
              <MetricBlock
                metricKey={block.key}
                unit={metricConfig.unit}
                primary={primary}
                secondary={secondary}
                primaryLabel={previousYear}
                secondaryLabel={currentYear}
                change={metricConfig.change}
                cohortChange={metricConfig.cohortChange}
                cohortName={cohortName}
                isRate={isRate}
              />
            </Box>
          );
        })}
      </Stack>
      {isRestricted && (
        <Stack direction="row" sx={{ alignItems: 'center', gap: 1, px: 1.5 }}>
          <Icons.Warning
            customClass="size-4 shrink-0 text-amber-700"
            aria-hidden="true"
          />
          <span className="text-sm">
            This critical element is not available higher in the funnel.
          </span>
        </Stack>
      )}
    </Box>
  );
};

export default CriticalElementChart;
